import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getToken, getRole, logout } from "../utils/auth";
import HumanLoader from "../components/loaders/HumanLoader";
import "./CategoryListPage.css";

export default function CategoryListPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const role = getRole();

  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newCategory, setNewCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const timeoutRef = useRef(null);

  /* ---------- FETCH CATEGORIES ---------- */
  const fetchCategories = useCallback(async (signal) => {
    setPageLoading(true);
    setErrorMessage("");
    const startTime = Date.now();

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/categories?courseId=${courseId}`,
        {
          signal,
          headers: {
            Authorization: `Bearer ${getToken()}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (res.status === 401 || res.status === 403) {
        logout();
        navigate("/login");
        return;
      }

      if (!res.ok) {
        throw new Error(`Failed to load categories (HTTP ${res.status})`);
      }

      const data = await res.json();
      setCategories(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error(err);
        setErrorMessage(err.message || "Failed to fetch categories.");
      }
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(3000 - elapsed, 0);

      timeoutRef.current = setTimeout(() => {
        setPageLoading(false);
      }, remaining);
    }
  }, [courseId, navigate]);

  useEffect(() => {
    const controller = new AbortController();
    fetchCategories(controller.signal);

    return () => {
      controller.abort();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [fetchCategories]);

  /* ---------- ESC KEY LISTENER FOR MODALS ---------- */
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setSelectedCategory(null);
        setShowAddDialog(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  /* ---------- ADD CATEGORY (ADMIN ONLY) ---------- */
  const handleAddCategory = async (e) => {
    e.preventDefault();
    const trimmed = newCategory.trim();
    if (!trimmed) return;

    setLoading(true);
    setErrorMessage("");

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/categories`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`
          },
          body: JSON.stringify({
            name: trimmed,
            courseId
          })
        }
      );

      if (res.status === 401 || res.status === 403) {
        logout();
        navigate("/login");
        return;
      }

      if (!res.ok) {
        throw new Error("Unable to create category. Please try again.");
      }

      setNewCategory("");
      setShowAddDialog(false);
      fetchCategories();
    } catch (err) {
      console.error(err);
      setErrorMessage(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  /* ---------- LOADER ---------- */
  if (pageLoading) {
    return (
      <HumanLoader
        loadingText="Preparing categories"
        successText="Ready to practice!"
        duration={2000}
      />
    );
  }

  return (
    <div className="cat-layout">
      <div className="cat-container">
        {/* Navigation & Header */}
        <header className="cat-header">
          <div className="cat-title-group">
            <span className="cat-kicker">Curriculum Track</span>
            <h1 className="cat-title">Select Category</h1>
            <p className="cat-subtitle">
              Choose a specific module to begin target drills and monitor skill retention.
            </p>
          </div>

          {role === "admin" && (
            <button
              type="button"
              className="cat-btn cat-btn-primary"
              onClick={() => setShowAddDialog(true)}
            >
              <span className="cat-btn-icon">+</span> Add Category
            </button>
          )}
        </header>

        {/* Status & Error Alerts */}
        {errorMessage && (
          <div className="cat-alert cat-alert-error" role="alert">
            <span>{errorMessage}</span>
            <button onClick={() => setErrorMessage("")} className="cat-alert-close">
              ×
            </button>
          </div>
        )}

        {/* Metric Cards */}
        <section className="cat-metrics">
          <div className="cat-metric-card">
            <span className="cat-metric-label">Total Categories</span>
            <span className="cat-metric-value">{categories.length}</span>
          </div>
          <div className="cat-metric-card">
            <span className="cat-metric-label">Course Status</span>
            <span className="cat-metric-value cat-metric-status">
              <span
                className={`cat-status-dot ${
                  categories.length ? "cat-status-active" : "cat-status-pending"
                }`}
              />
              {categories.length ? "Active" : "No Modules"}
            </span>
          </div>
        </section>

        {/* Content Body */}
        {categories.length === 0 ? (
          <section className="cat-empty-state">
            <div className="cat-empty-icon" aria-hidden="true">📂</div>
            <h3>No categories available</h3>
            <p>This course doesn't have any practice categories configured yet.</p>
            {role === "admin" && (
              <button
                type="button"
                className="cat-btn cat-btn-primary"
                onClick={() => setShowAddDialog(true)}
              >
                Create First Category
              </button>
            )}
          </section>
        ) : (
          <section className="cat-grid">
            {categories.map((cat, index) => (
              <article key={cat._id} className="cat-card">
                <div className="cat-card-header">
                  <span className="cat-badge">Module {index + 1}</span>
                </div>
                <div className="cat-card-body">
                  <h2 className="cat-card-title">{cat.name}</h2>
                  <p className="cat-card-desc">
                    Targeted practice sets and performance telemetry for this track.
                  </p>
                </div>
                <div className="cat-card-footer">
                  <button
                    type="button"
                    className="cat-btn cat-btn-card"
                    onClick={() => setSelectedCategory(cat)}
                  >
                    View Questions
                    <span className="cat-arrow" aria-hidden="true">→</span>
                  </button>
                </div>
              </article>
            ))}
          </section>
        )}
      </div>

      {/* ---------- CONFIRM PROCEED MODAL ---------- */}
      {selectedCategory && (
        <div
          className="cat-modal-backdrop"
          onClick={() => setSelectedCategory(null)}
          role="presentation"
        >
          <div
            className="cat-modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cat-modal-header">
              <h3 id="confirm-modal-title">{selectedCategory.name}</h3>
              <p>Ready to start practicing this category?</p>
            </div>
            <div className="cat-modal-footer">
              <button
                type="button"
                className="cat-btn cat-btn-ghost"
                onClick={() => setSelectedCategory(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="cat-btn cat-btn-primary"
                onClick={() => navigate(`/categories/${selectedCategory._id}`)}
              >
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------- ADD CATEGORY MODAL (ADMIN ONLY) ---------- */}
      {role === "admin" && showAddDialog && (
        <div
          className="cat-modal-backdrop"
          onClick={() => setShowAddDialog(false)}
          role="presentation"
        >
          <div
            className="cat-modal-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <form onSubmit={handleAddCategory}>
              <div className="cat-modal-header">
                <h3 id="add-modal-title">Create Category</h3>
                <p>Add a new topic module to this course syllabus.</p>
              </div>

              <div className="cat-modal-body">
                <label htmlFor="cat-name-input" className="cat-label">
                  Category Name
                </label>
                <input
                  id="cat-name-input"
                  type="text"
                  className="cat-input"
                  placeholder="e.g. Data Structures & Algorithms"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  autoFocus
                  required
                />
              </div>

              <div className="cat-modal-footer">
                <button
                  type="button"
                  className="cat-btn cat-btn-ghost"
                  onClick={() => setShowAddDialog(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="cat-btn cat-btn-primary"
                  disabled={loading || !newCategory.trim()}
                >
                  {loading ? "Creating..." : "Save Category"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}